/**
 * Stage presentational bits, copied from site/src/components/stage/bits.tsx (P2 plan §2b, option 3).
 * Diffs: Avatar takes any {initials,color} (a US tech row), Money takes integer cents, Pill tone type exported.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '@ui/Icon';
import type { IconName } from '@ui/icons';
import { money } from '@app/core/format';
import type { Tone } from '@app/core/job_status';

export type { Tone };

export function Pill({ tone = 'neutral', children, live, title }: { tone?: Tone; children: ReactNode; live?: boolean; title?: string }) {
  return (
    <span className={`stg-pill stg-pill--${tone}`} title={title}>
      {live && <i className="stg-live" aria-hidden />}
      {children}
    </span>
  );
}

export function Avatar({ tech, size = 26 }: { tech: { initials: string; color: string } | null | undefined; size?: number }) {
  if (!tech) return <span className="stg-avatar" style={{ ['--c' as string]: '#27466a', width: size, height: size, fontSize: size * 0.4 }} aria-hidden>·</span>;
  return (
    <span className="stg-avatar" style={{ ['--c' as string]: tech.color, width: size, height: size, fontSize: size * 0.4 }} aria-hidden>
      {tech.initials}
    </span>
  );
}

export function Card({ title, icon, right, children, focus, className, coach, ...rest }: { title?: ReactNode; icon?: IconName; right?: ReactNode; children?: ReactNode; focus?: string; className?: string; coach?: string; id?: string }) {
  return (
    <section className={['stg-card', className].filter(Boolean).join(' ')} data-focus={focus} data-coach={coach} {...rest}>
      {(title || right) && (
        <header className="stg-card__head">
          {icon && <Icon name={icon} size={14} />}
          <h3>{title}</h3>
          {right && <div className="stg-card__right">{right}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/** Animated money counter over integer cents (tweens toward the target; instant under reduced motion). */
export function Money({ value, cents = false, reduced }: { value: number; cents?: boolean; reduced: boolean }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const cur = useRef(value);
  useEffect(() => {
    if (reduced || Math.abs(value - cur.current) < 0.5) { cur.current = value; setShown(value); return; }
    from.current = cur.current;
    const start = performance.now();
    const dur = value > from.current ? 900 : 200;
    let raf = 0;
    const step = (now: number) => {
      const f = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - f, 3);
      cur.current = from.current + (value - from.current) * e;
      setShown(cur.current);
      if (f < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, reduced]);
  return <>{money(Math.max(0, Math.round(shown)), { cents })}</>;
}

export function Empty({ icon = 'clock', children }: { icon?: IconName; children: ReactNode }) {
  return (
    <div className="stg-empty">
      <Icon name={icon} size={18} />
      <p>{children}</p>
    </div>
  );
}

export function Kv({ k, v, fresh, mono }: { k: string; v: ReactNode; fresh?: boolean; mono?: boolean }) {
  return (
    <div className={['stg-kv', fresh && 'is-fresh', v == null && 'is-pending'].filter(Boolean).join(' ')}>
      <dt>{k}</dt>
      <dd className={mono ? 'mono' : undefined}>{v ?? <span className="stg-dots" aria-label="listening"><i /><i /><i /></span>}</dd>
    </div>
  );
}

export function Info({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="stg-info">
      <button type="button" aria-label="How this is calculated" aria-expanded={open} onClick={() => setOpen((o) => !o)} onBlur={() => setOpen(false)}>
        <Icon name="info" size={13} />
      </button>
      {open && <span role="tooltip" className="stg-info__tip">{text}</span>}
    </span>
  );
}

export function QrBadge() {
  return (
    <span className="stg-qr" title="QR-verified genuine part">
      <Icon name="qr" size={12} /> Verified
    </span>
  );
}
